<script setup lang="ts">
/** "โน้ตภายใน" section of the customer tab: every note about this customer (all their chats), newest first. */
import { computed, nextTick, ref, watch } from 'vue'
import type { Note } from '../../api/collab/types'
import type { ConversationDetail } from '../../api/types'
import { formatAgo } from '../../lib/inbox'
import { useClock } from '../../stores/clock'
import { useCollab } from '../../stores/collab'
import { useInbox } from '../../stores/inbox'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ detail: ConversationDetail }>()
const collab = useCollab()
const inbox = useInbox()
const clock = useClock()

const SHOW = 3
const all = computed(() => collab.notesFor(props.detail.id))
const expanded = ref(false)
const shown = computed(() => (expanded.value ? all.value : all.value.slice(0, SHOW)))
const text = ref('')
const box = ref<HTMLTextAreaElement | null>(null)
const editingId = ref<string | null>(null)
const editText = ref('')

watch(
  () => props.detail.id,
  () => {
    expanded.value = false
    text.value = ''
    editingId.value = null
  },
)

function autosize(el: HTMLTextAreaElement | null): void {
  if (!el) return
  el.style.height = 'auto'
  el.style.height = `${Math.min(el.scrollHeight, 140)}px`
}

async function add(): Promise<void> {
  if (!text.value.trim() || collab.busy) return // Enter twice while saving: one note
  if (await collab.addNote(props.detail.id, text.value)) {
    text.value = ''
    void nextTick(() => autosize(box.value))
  }
}

function onKey(e: KeyboardEvent): void {
  if (e.isComposing) return
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    void add()
  }
}

function startEdit(n: Note): void {
  editingId.value = n.id
  editText.value = n.text
}

async function saveEdit(n: Note): Promise<void> {
  const t = editText.value.trim()
  if (!t || t === n.text) {
    editingId.value = null
    return
  }
  if (await collab.editNote(props.detail.id, n, t)) editingId.value = null
}

function onEditKey(e: KeyboardEvent, n: Note): void {
  if (e.isComposing) return
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    void saveEdit(n)
  } else if (e.key === 'Escape') {
    editingId.value = null
  }
}

const initial = (n: Note) => {
  const s = inbox.staffInfo(n.author.id)
  return s.initial !== '·' ? s.initial : n.author.name.slice(0, 1)
}
</script>

<template>
  <section class="cs" aria-label="โน้ตภายใน">
    <div class="sec-h">
      <Icon name="note" class="cn-ic" />
      <span class="sec-t">โน้ตภายใน</span>
      <span v-if="all.length" class="cn-n mono">{{ all.length }}</span>
      <span class="grow"></span>
      <span class="sec-s">เห็นเฉพาะพนักงาน</span>
    </div>

    <div class="cn-add">
      <textarea
        ref="box"
        v-model="text"
        rows="1"
        maxlength="1000"
        aria-label="เพิ่มโน้ตภายในเกี่ยวกับลูกค้า"
        placeholder="เพิ่มโน้ต เช่น แพ้กุ้ง สั่งต่อทุกสิ้นเดือน"
        @input="autosize(box)"
        @keydown="onKey"
      ></textarea>
      <button class="cn-save" :disabled="!text.trim() || collab.busy" @click="add">บันทึก</button>
    </div>

    <ul v-if="shown.length" class="cn-list">
      <li v-for="n in shown" :key="n.id" class="cn-i">
        <span class="av cn-av" :class="inbox.staffInfo(n.author.id).tone" aria-hidden="true">{{ initial(n) }}</span>
        <div class="cn-b">
          <div class="cn-meta">
            <b>{{ n.author.name }}</b>
            <span>{{ formatAgo(n.at, clock.now) }}</span>
            <span v-if="n.edited_at">· แก้ไขแล้ว</span>
            <span v-if="n.conversation_id !== detail.id">· จากอีกแชท</span>
            <span class="grow"></span>
            <template v-if="n.can_edit && editingId !== n.id">
              <button class="cn-ib" aria-label="แก้โน้ต" title="แก้โน้ต" @click="startEdit(n)"><Icon name="pencil" /></button>
              <button class="cn-ib" aria-label="ลบโน้ต" title="ลบโน้ต" @click="collab.deleteNote(detail.id, n)"><Icon name="trash" /></button>
            </template>
          </div>
          <p v-if="editingId !== n.id" class="cn-txt">{{ n.text }}</p>
          <div v-else class="cn-edit">
            <textarea v-model="editText" rows="2" aria-label="แก้โน้ตภายใน" @keydown="onEditKey($event, n)"></textarea>
            <div class="cn-ea">
              <button class="cn-x" @click="editingId = null">ยกเลิก</button>
              <button class="cn-save sm" :disabled="!editText.trim()" @click="saveEdit(n)">บันทึก</button>
            </div>
          </div>
        </div>
      </li>
    </ul>
    <p v-else-if="collab.notesLoaded[detail.id]" class="cn-empty">ยังไม่มีโน้ต จดสิ่งที่ทีมควรรู้เกี่ยวกับลูกค้าคนนี้ได้ที่นี่</p>
    <button v-if="all.length > SHOW" class="cn-more" @click="expanded = !expanded">
      {{ expanded ? 'ย่อ' : `ดูโน้ตทั้งหมด ${all.length} รายการ` }}
    </button>
  </section>
</template>

<style scoped>
.cn-ic {
  width: 16px;
  height: 16px;
  color: #a37e0b;
}
.cn-n {
  font-size: 12px;
  color: var(--mut);
}
.cn-add {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 6px 6px 6px 10px;
  border: 1px solid #ecdca0;
  border-radius: 10px;
  background: #fffbe6;
}
.cn-add:focus-within {
  border-color: #d6b23c;
}
.cn-add textarea {
  flex: 1;
  min-width: 0;
  min-height: 32px;
  max-height: 140px;
  padding: 5px 0;
  border: 0;
  background: transparent;
  font-size: 13.5px;
  line-height: 22px;
  resize: none;
  outline: none;
}
.cn-add textarea::placeholder {
  color: #8f7d45;
}
.cn-save {
  flex: none;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  background: var(--warn);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
}
.cn-save.sm {
  height: 28px;
}
.cn-save:disabled {
  background: #cdb88a;
}
.cn-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
}
.cn-i {
  display: flex;
  gap: 10px;
  padding: 8px 0;
}
.cn-i + .cn-i {
  border-top: 1px solid var(--line2);
}
.cn-av {
  width: 24px;
  height: 24px;
  font-size: 12px;
  margin-top: 1px;
}
.cn-b {
  flex: 1;
  min-width: 0;
}
.cn-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  white-space: nowrap;
}
.cn-meta b {
  font-weight: 600;
  color: var(--ink);
}
.cn-ib {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: var(--mut);
  opacity: 0;
}
.cn-i:hover .cn-ib,
.cn-ib:focus-visible {
  opacity: 1;
}
.cn-ib:hover {
  background: #eef2f0;
  color: var(--ink);
}
.cn-ib :deep(.ic) {
  width: 14px;
  height: 14px;
}
.cn-txt {
  font-size: 13.5px;
  line-height: 21px;
  color: #2e3b35;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.cn-edit textarea {
  display: block;
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #e3cf8c;
  border-radius: 8px;
  font-size: 13.5px;
  line-height: 21px;
  resize: vertical;
  outline: none;
}
.cn-ea {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 6px;
}
.cn-x {
  height: 28px;
  padding: 0 10px;
  border-radius: 7px;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.cn-x:hover {
  background: #eef2f0;
}
.cn-empty {
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.cn-more {
  align-self: flex-start;
  height: 28px;
  padding: 0 4px;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 19px;
  color: var(--ac2);
}
</style>
